/**
 * Admin V5 — Question Bank CMS  (Batch 2 · Phase 2).
 *
 * Full reusable question bank editor:
 *   · Search + filters (exam / section / skill / difficulty / status / q)
 *   · Bulk selection + bulk status change
 *   · CSV / JSON dry-run import + import with error report
 *   · Create / Edit / Preview
 *   · Quality-workflow actions: Submit for review · Approve · Request changes · Archive
 *   · Status counts summary
 */
import { useCallback, useEffect, useMemo, useRef, useState } from 'react';
import axios from 'axios';
import { toast } from 'sonner';
import { Loader2, Search, Upload, Download, RefreshCcw, Plus, Edit, Archive, Check, X, ClipboardList, Sparkles, Undo2 } from 'lucide-react';
import { Button } from '@/components/ui/button';
import { Card } from '@/components/ui/card';
import { Badge } from '@/components/ui/badge';
import { Input } from '@/components/ui/input';
import { Textarea } from '@/components/ui/textarea';
import { Label } from '@/components/ui/label';

const BACKEND_URL = process.env.REACT_APP_BACKEND_URL || window.location.origin;
const ax = axios.create({ baseURL: BACKEND_URL, withCredentials: true });

const STATUSES = ['draft', 'review', 'published', 'archived', 'inactive'];
const SECTIONS = { dsat: ['rw', 'math'] };

export default function AdminQuestionBankPanel() {
  const [filters, setFilters] = useState({ exam_type: 'dsat', section: '', status: 'review', skill: '', difficulty: '', q: '' });
  const [items, setItems] = useState([]);
  const [counts, setCounts] = useState({});
  const [total, setTotal] = useState(0);
  const [loading, setLoading] = useState(false);
  const [selected, setSelected] = useState(new Set());
  const [editing, setEditing] = useState(null);
  const [creating, setCreating] = useState(false);
  const [showImport, setShowImport] = useState(false);
  const [previewing, setPreviewing] = useState(null);
  const [bulkAction, setBulkAction] = useState(null); // 'approve' | 'publish' | 'request_changes' | 'archive' | 'reject'
  const [showAudit, setShowAudit] = useState(false);
  const [analytics, setAnalytics] = useState(null);

  const load = useCallback(async () => {
    setLoading(true);
    try {
      const q = Object.entries(filters).filter(([, v]) => v).map(([k, v]) => `${k}=${encodeURIComponent(v)}`).join('&');
      const [r, c, an] = await Promise.all([
        ax.get(`/api/exam/v2/questions?limit=200&${q}`),
        ax.get(`/api/exam/v2/questions/status-counts?exam_type=${filters.exam_type}`),
        ax.get(`/api/exam/v2/questions/analytics?exam_type=${filters.exam_type}`),
      ]);
      setItems(r.data?.items || []);
      setTotal(r.data?.total || 0);
      setCounts(c.data?.counts || {});
      setAnalytics(an.data || null);
    } catch (e) { toast.error(e.response?.data?.detail || 'Load failed'); }
    finally { setLoading(false); }
  }, [filters]);

  useEffect(() => { load(); }, [load]);

  const setFilter = (k, v) => setFilters((p) => ({ ...p, [k]: v }));
  const toggleSelect = (id) => setSelected((p) => { const n = new Set(p); n.has(id) ? n.delete(id) : n.add(id); return n; });
  const clearSelection = () => setSelected(new Set());

  const bulkStatus = async (to) => {
    if (!selected.size) return;
    if (!window.confirm(`Move ${selected.size} question(s) to "${to}"?`)) return;
    try {
      const r = await ax.post('/api/exam/v2/questions/bulk-status', { ids: Array.from(selected), to_status: to });
      toast.success(`${r.data?.updated}/${r.data?.requested} updated`);
      clearSelection();
      load();
    } catch (e) { toast.error(e.response?.data?.detail || 'Bulk update failed'); }
  };

  const transition = async (id, action, reason = null) => {
    try {
      await ax.post(`/api/exam/v2/questions/${id}/${action}`, { reason });
      toast.success(`${action.replace(/-/g, ' ')} · ok`);
      load();
    } catch (e) { toast.error(e.response?.data?.detail || 'Action failed'); }
  };

  const exportAll = async () => {
    try {
      const r = await ax.post('/api/exam/v2/questions/bulk-export', {}, { params: { exam_type: filters.exam_type, section: filters.section || undefined, status: filters.status || undefined } });
      const blob = new Blob([JSON.stringify(r.data.items, null, 2)], { type: 'application/json' });
      const url = URL.createObjectURL(blob);
      const a = document.createElement('a'); a.href = url; a.download = `question_bank_${filters.exam_type}.json`; a.click();
      URL.revokeObjectURL(url);
      toast.success(`Exported ${r.data.count} questions`);
    } catch (e) { toast.error(e.response?.data?.detail || 'Export failed'); }
  };

  return (
    <Card className="p-4" data-testid="admin-question-bank-panel">
      {/* Header */}
      <div className="flex items-center justify-between mb-4">
        <div>
          <div className="text-[10px] uppercase tracking-[3px] text-red-700 font-bold">WINHOUSE TUTORS</div>
          <h2 className="text-lg font-bold text-gray-900">Question Bank</h2>
          <div className="text-xs text-gray-500 mt-0.5">
            {Object.entries(counts).map(([k, v]) => <span key={k} className="mr-3">{k}: <span className="font-mono font-bold">{v}</span></span>)}
            {analytics?.avg_review_turnaround_hours != null && (
              <span className="ml-2 text-gray-500">· avg turnaround: <span className="font-mono">{analytics.avg_review_turnaround_hours}h</span> ({analytics.turnaround_sample_size} samples)</span>
            )}
            {analytics?.last_reviewer && (
              <span className="ml-2 text-gray-500">· last reviewer: <span className="font-mono">{analytics.last_reviewer}</span></span>
            )}
          </div>
        </div>
        <div className="flex gap-2">
          <Button size="sm" variant="outline" onClick={load} data-testid="qb-refresh"><RefreshCcw className="h-3.5 w-3.5 mr-1" /> Refresh</Button>
          <Button size="sm" variant="outline" onClick={() => setShowAudit(true)} data-testid="qb-open-audit"><Undo2 className="h-3.5 w-3.5 mr-1" /> Batch history</Button>
          <Button size="sm" variant="outline" onClick={exportAll} data-testid="qb-export"><Download className="h-3.5 w-3.5 mr-1" /> Export</Button>
          <Button size="sm" variant="outline" onClick={() => setShowImport(true)} data-testid="qb-open-import"><Upload className="h-3.5 w-3.5 mr-1" /> Bulk import</Button>
          <Button size="sm" onClick={() => setCreating(true)} className="bg-red-700 hover:bg-red-800 text-white" data-testid="qb-create-open"><Plus className="h-3.5 w-3.5 mr-1" /> New</Button>
        </div>
      </div>

      {/* Bulk actions on FILTERED set */}
      <div className="mb-3 p-2 border border-red-200 bg-red-50/40 rounded flex flex-wrap items-center gap-2 text-xs" data-testid="qb-bulk-filter-actions">
        <span className="font-bold text-gray-800">Bulk actions on current filter <span className="font-mono">({total})</span>:</span>
        <Button size="sm" variant="outline" onClick={() => setBulkAction('approve')} data-testid="qb-bulk-approve-filter"><Check className="h-3 w-3 mr-1 text-green-700" /> Approve all</Button>
        <Button size="sm" variant="outline" onClick={() => setBulkAction('publish')} data-testid="qb-bulk-publish-filter"><Sparkles className="h-3 w-3 mr-1 text-red-700" /> Publish all</Button>
        <Button size="sm" variant="outline" onClick={() => setBulkAction('request_changes')} data-testid="qb-bulk-request-changes-filter"><X className="h-3 w-3 mr-1 text-yellow-700" /> Request changes</Button>
        <Button size="sm" variant="outline" onClick={() => setBulkAction('archive')} data-testid="qb-bulk-archive-filter"><Archive className="h-3 w-3 mr-1 text-gray-700" /> Archive</Button>
        <Button size="sm" variant="outline" onClick={() => setBulkAction('reject')} className="text-red-700"><X className="h-3 w-3 mr-1" /> Reject</Button>
      </div>

      {/* Filters */}
      <div className="grid grid-cols-2 sm:grid-cols-6 gap-2 mb-3">
        <select className="h-8 px-2 rounded border border-gray-300 text-xs" value={filters.exam_type} onChange={(e) => setFilter('exam_type', e.target.value)} data-testid="qb-filter-exam">
          {['dsat', 'gmat', 'gre', 'ielts', 'selective', 'vce', 'pte', 'toefl'].map((x) => <option key={x} value={x}>{x.toUpperCase()}</option>)}
        </select>
        <select className="h-8 px-2 rounded border border-gray-300 text-xs" value={filters.section} onChange={(e) => setFilter('section', e.target.value)} data-testid="qb-filter-section">
          <option value="">All sections</option>
          {(SECTIONS[filters.exam_type] || []).map((s) => <option key={s} value={s}>{s}</option>)}
        </select>
        <select className="h-8 px-2 rounded border border-gray-300 text-xs" value={filters.status} onChange={(e) => setFilter('status', e.target.value)} data-testid="qb-filter-status">
          <option value="">All statuses</option>
          {STATUSES.map((s) => <option key={s} value={s}>{s}</option>)}
        </select>
        <Input placeholder="Skill" value={filters.skill} onChange={(e) => setFilter('skill', e.target.value)} className="h-8 text-xs" data-testid="qb-filter-skill" />
        <select className="h-8 px-2 rounded border border-gray-300 text-xs" value={filters.difficulty} onChange={(e) => setFilter('difficulty', e.target.value)} data-testid="qb-filter-difficulty">
          <option value="">Any difficulty</option>
          {['easy', 'medium', 'hard'].map((d) => <option key={d} value={d}>{d}</option>)}
        </select>
        <div className="relative">
          <Search className="absolute left-2 top-2.5 h-3 w-3 text-gray-400" />
          <Input placeholder="Search prompt / passage / tag" value={filters.q} onChange={(e) => setFilter('q', e.target.value)} className="h-8 pl-6 text-xs" data-testid="qb-filter-q" />
        </div>
      </div>

      {/* Bulk actions */}
      {selected.size > 0 && (
        <div className="mb-2 p-2 bg-red-50 border border-red-200 rounded flex items-center gap-2 text-xs">
          <span className="font-bold">{selected.size} selected</span>
          <Button size="sm" variant="outline" onClick={() => bulkStatus('review')} data-testid="qb-bulk-review">→ Review</Button>
          <Button size="sm" variant="outline" onClick={() => bulkStatus('published')} data-testid="qb-bulk-publish">→ Publish</Button>
          <Button size="sm" variant="outline" onClick={() => bulkStatus('archived')} data-testid="qb-bulk-archive">→ Archive</Button>
          <Button size="sm" variant="outline" onClick={clearSelection} className="ml-auto">Clear</Button>
        </div>
      )}

      {/* Grid */}
      {loading ? (
        <div className="py-8 flex items-center justify-center text-gray-500 gap-2"><Loader2 className="h-4 w-4 animate-spin" /> Loading…</div>
      ) : (
        <div className="border border-gray-200 rounded-lg overflow-hidden">
          <table className="w-full text-xs">
            <thead className="bg-gray-50 uppercase tracking-wider text-gray-500">
              <tr>
                <th className="p-2 w-8"></th>
                <th className="p-2 text-left">Prompt</th>
                <th className="p-2">Section</th>
                <th className="p-2">Skill</th>
                <th className="p-2">Difficulty</th>
                <th className="p-2">Type</th>
                <th className="p-2">Status</th>
                <th className="p-2">Version</th>
                <th className="p-2"></th>
              </tr>
            </thead>
            <tbody>
              {items.map((q) => (
                <tr key={q.question_id} className="border-t border-gray-100 hover:bg-gray-50" data-testid={`qb-row-${q.question_id}`}>
                  <td className="p-2"><input type="checkbox" checked={selected.has(q.question_id)} onChange={() => toggleSelect(q.question_id)} data-testid={`qb-select-${q.question_id}`} /></td>
                  <td className="p-2 max-w-md truncate">{q.prompt}</td>
                  <td className="p-2 text-center"><Badge variant="outline" className="text-[10px]">{q.section}</Badge></td>
                  <td className="p-2">{q.skill || '—'}</td>
                  <td className="p-2 text-center">{q.difficulty}</td>
                  <td className="p-2 text-center">{q.question_type}</td>
                  <td className="p-2 text-center">
                    <Badge className={`text-[10px] ${q.status === 'published' ? 'bg-green-600' : q.status === 'review' ? 'bg-yellow-500' : q.status === 'archived' ? 'bg-gray-500' : 'bg-blue-500'}`}>{q.status}</Badge>
                  </td>
                  <td className="p-2 text-center font-mono">{q.version}</td>
                  <td className="p-2 text-right">
                    <div className="flex gap-1 justify-end">
                      <Button size="sm" variant="outline" onClick={() => setPreviewing(q)} data-testid={`qb-preview-${q.question_id}`}><ClipboardList className="h-3 w-3" /></Button>
                      <Button size="sm" variant="outline" onClick={() => setEditing(q)} data-testid={`qb-edit-${q.question_id}`}><Edit className="h-3 w-3" /></Button>
                      {q.status === 'draft' && <Button size="sm" variant="outline" onClick={() => transition(q.question_id, 'submit-for-review')} data-testid={`qb-submit-review-${q.question_id}`}>→ Review</Button>}
                      {q.status === 'review' && <><Button size="sm" onClick={() => transition(q.question_id, 'approve', 'inline approve')} className="bg-green-700 hover:bg-green-800 text-white" data-testid={`qb-approve-${q.question_id}`}><Check className="h-3 w-3" /></Button>
                        <Button size="sm" variant="outline" onClick={() => { const r = prompt('Reason for change request?'); if (r) transition(q.question_id, 'request-changes', r); }} data-testid={`qb-request-changes-${q.question_id}`}><X className="h-3 w-3" /></Button></>}
                      {q.status !== 'archived' && <Button size="sm" variant="outline" onClick={() => transition(q.question_id, 'archive', 'archived from grid')} data-testid={`qb-archive-${q.question_id}`}><Archive className="h-3 w-3" /></Button>}
                    </div>
                  </td>
                </tr>
              ))}
              {items.length === 0 && <tr><td colSpan={9} className="p-8 text-center text-gray-500">No questions match your filters.</td></tr>}
            </tbody>
          </table>
        </div>
      )}
      <div className="mt-2 text-[10px] text-gray-500">Showing {items.length} of {total}. Only <b>published</b> questions appear in student tests.</div>

      {/* Modals */}
      {creating && <QuestionEditor onClose={() => setCreating(false)} onSaved={() => { setCreating(false); load(); }} defaults={{ exam_type: filters.exam_type }} />}
      {editing && <QuestionEditor question={editing} onClose={() => setEditing(null)} onSaved={() => { setEditing(null); load(); }} />}
      {showImport && <ImportModal onClose={() => setShowImport(false)} onDone={() => { setShowImport(false); load(); }} defaults={{ exam_type: filters.exam_type }} />}
      {previewing && <PreviewModal q={previewing} onClose={() => setPreviewing(null)} />}
      {bulkAction && <BulkActionModal action={bulkAction} filter={filters} onClose={() => setBulkAction(null)} onDone={() => { setBulkAction(null); load(); }} />}
      {showAudit && <BatchHistoryModal onClose={() => setShowAudit(false)} onRolledBack={() => { setShowAudit(false); load(); }} />}
    </Card>
  );
}

function QuestionEditor({ question, defaults, onClose, onSaved }) {
  const [form, setForm] = useState(question || {
    exam_type: defaults?.exam_type || 'dsat', section: 'rw', module: 'm1',
    domain: '', skill: '', difficulty: 'medium', question_type: 'mcq',
    prompt: '', passage: '', options: [{ key: 'A', text: '' }, { key: 'B', text: '' }, { key: 'C', text: '' }, { key: 'D', text: '' }],
    correct_answer: 'A', explanation: '', trap_explanation: '',
    estimated_time_seconds: 90, calculator_allowed: false, adaptive_band: 'standard',
    status: 'draft', source_type: 'winhouse_original', tags: [],
  });
  const [saving, setSaving] = useState(false);
  const set = (k, v) => setForm((p) => ({ ...p, [k]: v }));
  const setOpt = (i, txt) => setForm((p) => ({ ...p, options: p.options.map((o, j) => j === i ? { ...o, text: txt } : o) }));

  const save = async () => {
    setSaving(true);
    try {
      if (question) {
        await ax.patch(`/api/exam/v2/questions/${question.question_id}`, form);
        toast.success('Saved');
      } else {
        await ax.post('/api/exam/v2/questions', form);
        toast.success('Created');
      }
      onSaved();
    } catch (e) { toast.error(e.response?.data?.detail || 'Save failed'); }
    finally { setSaving(false); }
  };

  return (
    <div className="fixed inset-0 z-50 bg-black/60 flex items-center justify-center p-4" onClick={onClose} data-testid="qb-editor-modal">
      <div className="bg-white rounded-2xl max-w-3xl w-full p-6 max-h-[92vh] overflow-y-auto" onClick={(e) => e.stopPropagation()}>
        <div className="flex items-start justify-between">
          <h3 className="text-lg font-bold">{question ? 'Edit question' : 'New question'}</h3>
          <button onClick={onClose}><X className="h-4 w-4" /></button>
        </div>
        <div className="mt-4 grid sm:grid-cols-3 gap-2">
          <Field label="Exam"><Input value={form.exam_type} onChange={(e) => set('exam_type', e.target.value)} data-testid="qb-editor-exam" /></Field>
          <Field label="Section"><Input value={form.section} onChange={(e) => set('section', e.target.value)} data-testid="qb-editor-section" /></Field>
          <Field label="Module"><Input value={form.module || ''} onChange={(e) => set('module', e.target.value)} data-testid="qb-editor-module" /></Field>
          <Field label="Domain"><Input value={form.domain || ''} onChange={(e) => set('domain', e.target.value)} /></Field>
          <Field label="Skill"><Input value={form.skill || ''} onChange={(e) => set('skill', e.target.value)} /></Field>
          <Field label="Difficulty">
            <select value={form.difficulty} onChange={(e) => set('difficulty', e.target.value)} className="w-full h-9 px-2 rounded border border-gray-300 text-sm">{['easy', 'medium', 'hard'].map((d) => <option key={d}>{d}</option>)}</select>
          </Field>
          <Field label="Type">
            <select value={form.question_type} onChange={(e) => set('question_type', e.target.value)} className="w-full h-9 px-2 rounded border border-gray-300 text-sm">{['mcq', 'spr', 'tf'].map((d) => <option key={d}>{d}</option>)}</select>
          </Field>
          <Field label="Adaptive band">
            <select value={form.adaptive_band || 'standard'} onChange={(e) => set('adaptive_band', e.target.value)} className="w-full h-9 px-2 rounded border border-gray-300 text-sm">{['standard', 'higher'].map((d) => <option key={d}>{d}</option>)}</select>
          </Field>
          <Field label="Est. seconds"><Input type="number" value={form.estimated_time_seconds || 90} onChange={(e) => set('estimated_time_seconds', parseInt(e.target.value, 10))} /></Field>
        </div>
        <Field label="Passage / stimulus (optional)"><Textarea rows={3} value={form.passage || ''} onChange={(e) => set('passage', e.target.value)} data-testid="qb-editor-passage" /></Field>
        <Field label="Prompt"><Textarea rows={2} value={form.prompt || ''} onChange={(e) => set('prompt', e.target.value)} data-testid="qb-editor-prompt" /></Field>
        {form.question_type === 'mcq' && (
          <>
            <div className="grid sm:grid-cols-2 gap-2 mt-2">
              {form.options.map((o, i) => (
                <Field key={o.key} label={`Option ${o.key}`}>
                  <Input value={o.text} onChange={(e) => setOpt(i, e.target.value)} data-testid={`qb-editor-opt-${o.key}`} />
                </Field>
              ))}
            </div>
            <Field label="Correct answer key (A/B/C/D)"><Input value={form.correct_answer || 'A'} onChange={(e) => set('correct_answer', e.target.value.toUpperCase())} data-testid="qb-editor-correct" /></Field>
          </>
        )}
        {form.question_type === 'spr' && (
          <Field label="Correct answer (numeric)"><Input value={form.correct_answer ?? ''} onChange={(e) => set('correct_answer', e.target.value)} data-testid="qb-editor-correct-spr" /></Field>
        )}
        <Field label="Explanation"><Textarea rows={2} value={form.explanation || ''} onChange={(e) => set('explanation', e.target.value)} data-testid="qb-editor-explanation" /></Field>
        <Field label="Trap explanation (why a distractor is tempting)"><Textarea rows={2} value={form.trap_explanation || ''} onChange={(e) => set('trap_explanation', e.target.value)} /></Field>
        <div className="mt-4 flex justify-end gap-2">
          <Button variant="outline" onClick={onClose}>Cancel</Button>
          <Button onClick={save} disabled={saving} className="bg-red-700 hover:bg-red-800 text-white" data-testid="qb-editor-save">
            {saving ? <Loader2 className="h-4 w-4 animate-spin mr-1" /> : <Check className="h-4 w-4 mr-1" />} Save
          </Button>
        </div>
      </div>
    </div>
  );
}

function ImportModal({ defaults, onClose, onDone }) {
  const [file, setFile] = useState(null);
  const [dryRun, setDryRun] = useState(true);
  const [running, setRunning] = useState(false);
  const [report, setReport] = useState(null);
  const inputRef = useRef();

  const run = async () => {
    if (!file) return;
    setRunning(true); setReport(null);
    try {
      const fd = new FormData();
      fd.append('file', file);
      fd.append('exam_type', defaults.exam_type);
      fd.append('status', 'review');
      fd.append('dry_run', dryRun ? 'true' : 'false');
      const r = await ax.post('/api/exam/v2/questions/bulk-import', fd, { headers: { 'Content-Type': 'multipart/form-data' } });
      setReport(r.data);
      if (!dryRun) { toast.success(`Imported ${r.data.inserted} questions`); onDone(); }
      else toast.success(`Dry run: ${r.data.would_insert} valid, ${(r.data.errors || []).length} errors`);
    } catch (e) { toast.error(e.response?.data?.detail || 'Import failed'); }
    finally { setRunning(false); }
  };

  return (
    <div className="fixed inset-0 z-50 bg-black/60 flex items-center justify-center p-4" onClick={onClose} data-testid="qb-import-modal">
      <div className="bg-white rounded-2xl max-w-xl w-full p-6" onClick={(e) => e.stopPropagation()}>
        <div className="flex items-start justify-between"><h3 className="text-lg font-bold">Bulk import</h3><button onClick={onClose}><X className="h-4 w-4" /></button></div>
        <p className="text-xs text-gray-500 mt-2">CSV or JSON. Fields: prompt, passage, options (JSON), correct_answer, explanation, section, domain, skill, difficulty, question_type, tags (JSON), estimated_time_seconds, calculator_allowed, adaptive_band.</p>
        <div className="mt-3">
          <input ref={inputRef} type="file" accept=".csv,.json,application/json,text/csv" onChange={(e) => setFile(e.target.files[0])} data-testid="qb-import-file" />
        </div>
        <div className="mt-3 flex items-center gap-2 text-xs">
          <input id="qb-dryrun" type="checkbox" checked={dryRun} onChange={(e) => setDryRun(e.target.checked)} data-testid="qb-import-dry" />
          <label htmlFor="qb-dryrun">Dry run first (validate + show error report, no writes)</label>
        </div>
        {report && (
          <div className="mt-3 p-3 bg-gray-50 rounded border border-gray-200 text-xs max-h-56 overflow-y-auto" data-testid="qb-import-report">
            <div><b>{dryRun ? `Would insert: ${report.would_insert}` : `Inserted: ${report.inserted}`}</b></div>
            {(report.errors || []).length > 0 && (
              <div className="mt-2">
                <div className="font-bold text-red-700">Errors ({report.errors.length}):</div>
                {report.errors.slice(0, 10).map((e) => <div key={e.row} className="text-red-700">row {e.row}: {e.error}</div>)}
              </div>
            )}
          </div>
        )}
        <div className="mt-4 flex justify-end gap-2">
          <Button variant="outline" onClick={onClose}>Close</Button>
          <Button onClick={run} disabled={!file || running} className="bg-red-700 hover:bg-red-800 text-white" data-testid="qb-import-run">
            {running ? <Loader2 className="h-4 w-4 animate-spin mr-1" /> : <Upload className="h-4 w-4 mr-1" />} {dryRun ? 'Run dry-run' : 'Import now'}
          </Button>
        </div>
      </div>
    </div>
  );
}

function PreviewModal({ q, onClose }) {
  return (
    <div className="fixed inset-0 z-50 bg-black/60 flex items-center justify-center p-4" onClick={onClose} data-testid="qb-preview-modal">
      <div className="bg-white rounded-2xl max-w-2xl w-full p-6 max-h-[92vh] overflow-y-auto" onClick={(e) => e.stopPropagation()}>
        <div className="flex items-start justify-between"><h3 className="text-lg font-bold">Preview</h3><button onClick={onClose}><X className="h-4 w-4" /></button></div>
        <div className="mt-3 text-[10px] uppercase text-gray-500 font-bold tracking-wider">{q.section} · {q.domain} · {q.skill} · {q.difficulty}</div>
        {q.passage && <div className="mt-3 p-3 rounded bg-gray-50 border text-sm whitespace-pre-wrap">{q.passage}</div>}
        <div className="mt-3 text-base font-medium whitespace-pre-wrap">{q.prompt}</div>
        {q.options && (
          <div className="mt-3 space-y-1">
            {q.options.map((o) => (
              <div key={o.key} className={`p-2 rounded border text-sm ${o.key === q.correct_answer ? 'border-green-500 bg-green-50' : 'border-gray-200'}`}>
                <span className="font-mono font-bold mr-2">{o.key}</span>{o.text}
                {o.key === q.correct_answer && <Badge className="ml-2 bg-green-600 text-[9px]">Correct</Badge>}
              </div>
            ))}
          </div>
        )}
        {q.explanation && <div className="mt-3 p-3 rounded bg-gray-50 text-xs"><b>Explanation.</b> {q.explanation}</div>}
        {q.trap_explanation && <div className="mt-2 p-3 rounded bg-yellow-50 text-xs"><b>Trap.</b> {q.trap_explanation}</div>}
        {q.status_audit && q.status_audit.length > 0 && (
          <div className="mt-3">
            <div className="text-[10px] uppercase text-gray-500 font-bold tracking-wider">Audit trail</div>
            {q.status_audit.map((e, i) => (
              <div key={i} className="text-[11px] text-gray-600 mt-1">{e.at?.split('T')[0]} · {e.by} · {e.from} → {e.to}{e.reason ? ` · ${e.reason}` : ''}{e.override ? ' · override' : ''}</div>
            ))}
          </div>
        )}
      </div>
    </div>
  );
}

function Field({ label, children }) {
  return (
    <div className="mt-2">
      <Label className="text-[10px] uppercase text-gray-500 font-bold tracking-wider block mb-1">{label}</Label>
      {children}
    </div>
  );
}

// ============================================================================
// BulkActionModal — preview, spot-check, confirm
// ============================================================================
const ACTION_LABEL = { approve: 'Approve', publish: 'Publish', request_changes: 'Request changes', archive: 'Archive', reject: 'Reject' };

function BulkActionModal({ action, filter, onClose, onDone }) {
  const [preview, setPreview] = useState(null);
  const [spotCheck, setSpotCheck] = useState(null);
  const [reason, setReason] = useState('');
  const [busy, setBusy] = useState(false);
  const [confirming, setConfirming] = useState(false);

  useEffect(() => {
    (async () => {
      try {
        // For approve/publish, restrict preview to items in the correct current status
        const preFilter = { ...filter };
        if (action === 'publish') preFilter.status = preFilter.status || 'approved';
        const r = await ax.post('/api/exam/v2/questions/bulk-preview', { filter: preFilter });
        setPreview(r.data);
      } catch (e) { toast.error(e.response?.data?.detail || 'Preview failed'); }
    })();
  }, [action, filter]);

  const runSpotCheck = async () => {
    try {
      const preFilter = { ...filter };
      if (action === 'publish') preFilter.status = preFilter.status || 'approved';
      const r = await ax.post('/api/exam/v2/questions/spot-check?n=10', { filter: preFilter });
      setSpotCheck(r.data.items || []);
    } catch (e) { toast.error(e.response?.data?.detail || 'Spot-check failed'); }
  };

  const execute = async () => {
    if (!reason.trim()) { toast.error('Reason is required'); return; }
    setBusy(true);
    try {
      const preFilter = { ...filter };
      if (action === 'publish') preFilter.status = preFilter.status || 'approved';
      const r = await ax.post('/api/exam/v2/questions/bulk-transition', {
        action, reason, filter: preFilter,
      });
      toast.success(`${ACTION_LABEL[action]}: ${r.data.moved} moved · batch ${r.data.batch_id} (rollback within ${r.data.rollback_window_hours}h)`);
      onDone();
    } catch (e) { toast.error(e.response?.data?.detail || 'Execute failed'); }
    finally { setBusy(false); setConfirming(false); }
  };

  return (
    <div className="fixed inset-0 z-50 bg-black/60 flex items-center justify-center p-4" onClick={onClose} data-testid="qb-bulk-modal">
      <div className="bg-white rounded-2xl max-w-2xl w-full p-6 max-h-[92vh] overflow-y-auto" onClick={(e) => e.stopPropagation()}>
        <div className="flex items-start justify-between">
          <div>
            <div className="text-[10px] uppercase tracking-[3px] text-red-700 font-bold">WINHOUSE TUTORS</div>
            <h3 className="text-lg font-bold">{ACTION_LABEL[action]} — bulk</h3>
            <div className="text-xs text-gray-500 mt-1">Preview of the filtered set. Nothing runs until you confirm.</div>
          </div>
          <button onClick={onClose}><X className="h-4 w-4" /></button>
        </div>

        {!preview ? (
          <div className="py-8 flex items-center justify-center gap-2 text-gray-500"><Loader2 className="h-4 w-4 animate-spin" /> Loading preview…</div>
        ) : (
          <>
            <div className="mt-4 grid grid-cols-2 gap-3 text-sm">
              <div><span className="text-gray-500">Total selected:</span> <span className="font-bold text-lg" data-testid="qb-bulk-count">{preview.count}</span></div>
              <div><span className="text-gray-500">Reading & Writing:</span> <span className="font-mono">{preview.rw_count}</span></div>
              <div><span className="text-gray-500">Math:</span> <span className="font-mono">{preview.math_count}</span></div>
              <div><span className="text-gray-500">Duplicates:</span> <span className={`font-mono ${preview.duplicate_warnings.length ? 'text-red-700 font-bold' : ''}`}>{preview.duplicate_warnings.length}</span></div>
            </div>
            <div className="mt-3 grid sm:grid-cols-2 gap-3 text-xs">
              <StatBlock label="Difficulty" data={preview.by_difficulty} />
              <StatBlock label="Domain" data={preview.by_domain} />
            </div>
            <div className="mt-3 grid sm:grid-cols-2 gap-3 text-xs">
              <StatBlock label="Skill" data={preview.by_skill} />
              <StatBlock label="Section" data={preview.by_section} />
            </div>

            <div className="mt-4 flex items-center gap-2">
              <Button size="sm" variant="outline" onClick={runSpotCheck} data-testid="qb-bulk-spot-check">
                <Sparkles className="h-3 w-3 mr-1" /> Review random 10
              </Button>
              {spotCheck && <span className="text-[11px] text-gray-500">Sampled {spotCheck.length} for spot check ↓</span>}
            </div>
            {spotCheck && (
              <div className="mt-2 max-h-40 overflow-y-auto border border-gray-200 rounded p-2 text-[11px] space-y-1" data-testid="qb-bulk-spot-results">
                {spotCheck.map((q) => (
                  <div key={q.question_id} className="pb-1 border-b border-gray-100">
                    <span className="font-mono text-gray-500">{q.question_id}</span> · <span className="font-semibold">{q.section}</span> · {q.skill} · {q.difficulty}
                    <div className="text-gray-700 truncate">{q.prompt}</div>
                  </div>
                ))}
              </div>
            )}

            <div className="mt-4">
              <Label className="text-[10px] uppercase text-gray-500 font-bold tracking-wider block mb-1">Reason (required, audit-logged)</Label>
              <Textarea rows={2} value={reason} onChange={(e) => setReason(e.target.value)} placeholder="e.g. Spot-checked 10 of 156, content approved for release" data-testid="qb-bulk-reason" />
            </div>

            {!confirming ? (
              <div className="mt-4 flex justify-end gap-2">
                <Button variant="outline" onClick={onClose}>Cancel</Button>
                <Button onClick={() => setConfirming(true)} disabled={!reason.trim() || preview.count === 0} className="bg-red-700 hover:bg-red-800 text-white" data-testid="qb-bulk-review-confirm">
                  Continue → confirm
                </Button>
              </div>
            ) : (
              <div className="mt-4 p-3 bg-yellow-50 border-2 border-yellow-400 rounded" data-testid="qb-bulk-final-confirm">
                <div className="text-sm font-bold text-yellow-900">Final confirmation</div>
                <div className="text-xs text-yellow-800 mt-1">This will {ACTION_LABEL[action].toLowerCase()} <b>{preview.count}</b> questions. A batch record will be created and can be rolled back within 24 hours.</div>
                <div className="mt-2 flex justify-end gap-2">
                  <Button variant="outline" onClick={() => setConfirming(false)}>Back</Button>
                  <Button onClick={execute} disabled={busy} className="bg-red-700 hover:bg-red-800 text-white" data-testid="qb-bulk-execute">
                    {busy ? <Loader2 className="h-4 w-4 animate-spin mr-1" /> : <Check className="h-4 w-4 mr-1" />} Confirm &amp; run
                  </Button>
                </div>
              </div>
            )}
          </>
        )}
      </div>
    </div>
  );
}

function StatBlock({ label, data }) {
  const rows = Object.entries(data || {}).sort((a, b) => b[1] - a[1]);
  if (!rows.length) return null;
  return (
    <div className="p-2 border border-gray-200 rounded bg-gray-50">
      <div className="text-[9px] uppercase text-gray-500 font-bold tracking-wider mb-1">{label}</div>
      {rows.slice(0, 8).map(([k, v]) => (
        <div key={k} className="flex justify-between border-b border-gray-100 py-0.5"><span className="text-gray-700">{k}</span><span className="font-mono">{v}</span></div>
      ))}
    </div>
  );
}

// ============================================================================
// BatchHistoryModal — list bulk-ops, rollback
// ============================================================================
function BatchHistoryModal({ onClose, onRolledBack }) {
  const [ops, setOps] = useState([]);
  const [loading, setLoading] = useState(true);
  const load = async () => {
    setLoading(true);
    try {
      const r = await ax.get('/api/exam/v2/questions/bulk-ops?limit=20');
      setOps(r.data?.items || []);
    } catch (e) { toast.error(e.response?.data?.detail || 'Load failed'); }
    finally { setLoading(false); }
  };
  useEffect(() => { load(); }, []);

  const rollback = async (batch_id) => {
    const reason = prompt('Rollback reason?');
    if (!reason) return;
    try {
      const r = await ax.post(`/api/exam/v2/questions/bulk-ops/${batch_id}/rollback`, { reason });
      toast.success(`Reverted ${r.data.reverted} questions`);
      onRolledBack();
    } catch (e) { toast.error(e.response?.data?.detail || 'Rollback failed'); }
  };

  return (
    <div className="fixed inset-0 z-50 bg-black/60 flex items-center justify-center p-4" onClick={onClose} data-testid="qb-audit-modal">
      <div className="bg-white rounded-2xl max-w-3xl w-full p-6 max-h-[92vh] overflow-y-auto" onClick={(e) => e.stopPropagation()}>
        <div className="flex items-start justify-between">
          <h3 className="text-lg font-bold">Batch history (last 20)</h3>
          <button onClick={onClose}><X className="h-4 w-4" /></button>
        </div>
        {loading ? <div className="py-8 flex items-center justify-center"><Loader2 className="h-4 w-4 animate-spin" /></div> : (
          <table className="w-full text-xs mt-3">
            <thead className="text-gray-500 uppercase tracking-wider"><tr><th className="p-1 text-left">Batch</th><th>Action</th><th>Count</th><th>By</th><th>When</th><th>Status</th><th></th></tr></thead>
            <tbody>
              {ops.map((o) => (
                <tr key={o.batch_id} className="border-t border-gray-100" data-testid={`qb-audit-row-${o.batch_id}`}>
                  <td className="p-1 font-mono">{o.batch_id}</td>
                  <td className="p-1 text-center">{o.action}</td>
                  <td className="p-1 text-center font-mono">{o.count}</td>
                  <td className="p-1 text-gray-600">{o.actor_email}</td>
                  <td className="p-1 text-gray-500 text-[10px]">{(o.at || '').replace('T', ' ').slice(0, 16)}</td>
                  <td className="p-1 text-center">
                    {o.rolled_back_at ? <Badge variant="outline" className="text-[9px]">Rolled back</Badge> : <Badge className="bg-green-600 text-[9px]">Active</Badge>}
                  </td>
                  <td className="p-1 text-right">
                    {!o.rolled_back_at && <Button size="sm" variant="outline" onClick={() => rollback(o.batch_id)} data-testid={`qb-rollback-${o.batch_id}`}><Undo2 className="h-3 w-3" /></Button>}
                  </td>
                </tr>
              ))}
              {ops.length === 0 && <tr><td colSpan={7} className="p-6 text-center text-gray-500">No bulk operations yet.</td></tr>}
            </tbody>
          </table>
        )}
      </div>
    </div>
  );
}
